import { getArticlesQL, getWingieArticlesQL } from 'services/articles'
import { CampaginAPI, BilgiAPI, InformationContentAPI, HomepageAPI, FlightAPI } from 'services/rest'
import { domainURL, type DomainURL } from 'utils/domainURL'

import { qlParser } from '../utils'
import { LRUCache } from '../../utils/lruCache'

// LRU Cache objeleri (max 100 entry, otomatik eski entry temizleme)
const campaignCache = new LRUCache<any>(100)
const articleCache = new LRUCache<any>(100)
const homepageCache = new LRUCache<any>(100)

const cacheTime = 300000 // 5 minutes in milliseconds

// Her 10 dakikada bir stale entry'leri temizle
setInterval(() => {
  campaignCache.cleanStaleEntries(cacheTime)
  articleCache.cleanStaleEntries(cacheTime)
  homepageCache.cleanStaleEntries(cacheTime)
}, 600000) // 10 minutes

export const getCampaigns = async ({ page = 1, limit = 100, market, language, domain }) => {
  // Cache key'e tüm parametreleri dahil et (market, domain, language - farklı kombinasyonlar farklı cache olmalı)
  const cacheKey = `${market}-${domain || market}-${language || 'default'}-page${page}-limit${limit}`
  const cachedResult = campaignCache.get(cacheKey)

  if (cachedResult?.staleTime) {
    const staleTime = cachedResult.staleTime
    const now = Date.now()
    if (now - staleTime < cacheTime) {
      console.log(`[TIMING getCampaigns cache hit Return (${cacheKey})] = `, Date.now() - staleTime)
      // 5 minutes in milliseconds - stale-while-revalidate pattern
      return cachedResult.data
    }
    // Cache stale - background'da güncelle, eski veriyi döndür
    const res = structuredClone(cachedResult)
    getCampaignsRequest({ page, limit, market, language, domain }) // Arka planda cache güncelleme
    return res.data
  }
  return await getCampaignsRequest({ page, limit, market, language, domain })
}

const getCampaignsRequest = async ({ page = 1, limit = 100, market, language, domain }) => {
  const timing = Date.now()
  console.log('[TIMING getCampaigns Start] = ', timing)
  if (market !== 'tr') return { campaigns: { collection: [] } }

  return await CampaginAPI.post('', {
    query: `
        query {
            campaigns(page: ${page}, limit:${limit}) {
                collection {
                    provider {
                        name
                        slug
                    }
                    id
                    title
                    slug
                    backgroundImageUrl
                    thumbnailImageUrl
                    endDate
                    tags {
                        id
                        _id
                        slug
                        name
                        color
                        priority
                    }
                }
            }
            providers {
                id
                name
                slug
                campaigns {
                    collection {
                        provider {
                            name
                            slug
                        }
                        id
                        title
                        slug
                        backgroundImageUrl
                        thumbnailImageUrl
                        endDate
                        tags {
                            id
                            _id
                            slug
                            name
                            color
                            priority
                        }
                    }
                }
            }
        }
    `,
  })
    .then(({ data }) => {
      console.log('[TIMING getCampaigns End] = ', Date.now() - timing)
      // Market, domain, language, page ve limit bazlı cache güncelleme
      const cacheKey = `${market}-${domain || market}-${language || 'default'}-page${page}-limit${limit}`
      campaignCache.set(cacheKey, { data: data.data, staleTime: timing })
      return data.data
    })
    .catch((error) => {
      console.log('error ->', error)
      if (error.response.status === 301) {
        return {
          status: error.response.status,
          redirectUrl: error.response.headers['redirect-to'],
        }
      } else if (error.response.status === 404) {
        return {
          status: error.response.status,
        }
      }
    })
}

type GetArticlesOptions = {
  topics?: string
  tags?: string
  limit?: number
  offset?: number
  popularPosts?: unknown
  language?: string
  market?: string
  domain?: string
}

export const GetArticles = async ({ topics = 'deneme', tags, limit = 6, offset = 0, popularPosts = null, language, market, domain }: GetArticlesOptions) => {
  // Articles için tüm parametreleri cache key'e dahil et (market, domain, language önemli!)
  const cacheKey = `${topics}-${tags || ''}-${market || 'default'}-${domain || market || 'default'}-${language || 'default'}-limit${limit}-offset${offset}-popular${JSON.stringify(
    popularPosts
  )}`
  const cachedResult = articleCache.get(cacheKey)

  if (cachedResult?.staleTime) {
    const staleTime = cachedResult.staleTime
    const now = Date.now()
    if (now - staleTime < cacheTime) {
      console.log(`[TIMING GetArticles cache hit Return (${cacheKey})] = `, Date.now() - staleTime)
      // 5 minutes in milliseconds - stale-while-revalidate pattern
      return cachedResult.data
    }
    // Cache stale - background'da güncelle, eski veriyi döndür
    const res = structuredClone(cachedResult)
    GetArticlesRequest({ topics, tags, limit, offset, popularPosts, language, market, domain }) // Arka planda cache güncelleme
    return res.data
  }
  return await GetArticlesRequest({ topics, tags, limit, offset, popularPosts, language, market, domain })
}

export const GetArticlesRequest = async ({ topics = 'deneme', tags, limit = 6, offset = 0, popularPosts = null, language, market, domain }: GetArticlesOptions) => {
  const timing = Date.now()
  console.log('[TIMING GetArticles Start] = ', timing)
  let variables = {
    postType: 1,
    popularPosts,
    topics,
    limit,
    offset,
    tags,
  }
  console.log('bilgi api ->', BilgiAPI.defaults.baseURL)
  return BilgiAPI.get(`${qlParser(getArticlesQL, variables)}`)
    .then(({ data }) => {
      const res = data.data
      const posts = res?.posts?.map((item) => {
        const resTopics = item.topics || []
        if (resTopics.length > 1) {
          const topicIdx = resTopics.findIndex((elem) => elem.slug === topics)
          if (topicIdx !== -1) {
            resTopics.unshift(resTopics[topicIdx])
            resTopics.splice(topicIdx + 1, 1)
          }
        }
        return item
      })
      console.log('[TIMING GetArticles End] = ', Date.now() - timing)
      // Tüm parametreleri cache key'e dahil et (market, domain, language dahil)
      const cacheKey = `${topics}-${tags || ''}-${market || 'default'}-${domain || market || 'default'}-${
        language || 'default'
      }-limit${limit}-offset${offset}-popular${JSON.stringify(popularPosts)}`
      articleCache.set(cacheKey, { data: { ...res, posts }, staleTime: timing })
      return { ...res, posts }
    })
    .catch((err) => {
      console.log(err)
    })
}

export const GetWingieArticles = async (locale: string, sort: [string] = ['updatedAt:desc']) => {
  const timing = Date.now()
  console.log('[TIMING GetWingieArticles Start] = ', timing)
  const variables = {
    ILocale: locale,
    locale,
    sort,
    filters: { author: { id: { notNull: true } } },
  }

  const CDN = 'https://cdn2.wingie.com'
  const authorImagePath = 'uploads/f_webp,s_175x175,fit_cover/'
  const articleImagePath = 'uploads/f_webp,s_352x153/'

  console.log('Information Content API base url -> ', InformationContentAPI.defaults.baseURL)

  try {
    const { data } = await InformationContentAPI.post('', {
      query: getWingieArticlesQL,
      variables,
    })
    console.log('[TIMING GetWingieArticles End] = ', Date.now() - timing)
    const mappedData = data?.data?.posts?.data?.reduce((acc, curr) => {
      const $topics = curr.attributes?.topics?.data
      const $author = curr.attributes?.author?.data?.attributes
      const $authorImage = curr.attributes?.author?.data?.attributes?.image?.data?.attributes?.url
      const $articleImage = curr.attributes?.image?.data?.attributes?.url
      const authorImageUrl = `${CDN}${$authorImage.replace('uploads/', authorImagePath)}`
      const articleImageUrl = `${CDN}${$articleImage.replace('uploads/', articleImagePath)}`

      const temp = {
        ...curr.attributes,
        author: {
          ...$author,
          authorInfo: {
            image: authorImageUrl,
          },
          image: authorImageUrl,
        },
        image: articleImageUrl,
        topics: $topics?.map((topic) => {
          return {
            ...topic.attributes,
            color: topic?.attributes?.badgeColor,
            title: topic?.attributes?.name,
          }
        }),
        title: curr.attributes?.name,
        updated: curr.attributes?.updatedAt,
      }
      acc.push(temp)

      return acc
    }, [])

    return { posts: mappedData ?? [] }
  } catch (error) {
    console.error('Wingie articles graphql request error ->', error)
    return { posts: [] }
  }
}

export const GetHomePageInitData = async ({ market, host, domain, language }) => {
  // Market, domain, host ve language'i cache key'e dahil et (language önemli - farklı diller farklı veri döndürebilir)
  const cacheKey = `${market}-${domain || market}-${language || 'default'}-${host || 'default'}`
  const cachedResult = homepageCache.get(cacheKey)

  if (cachedResult?.staleTime) {
    const staleTime = cachedResult.staleTime
    const now = Date.now()
    if (now - staleTime < cacheTime) {
      // 5 minutes in milliseconds - stale-while-revalidate pattern
      console.log(`[TIMING GetHomePageInitData cache hit Return (${cacheKey})] = `, Date.now() - staleTime)
      return cachedResult.data
    }
    // Cache stale - background'da güncelle, eski veriyi döndür
    const res = structuredClone(cachedResult)
    GetHomePageInitDataRequest({ market, host, domain, language }) // Arka planda cache güncelleme
    return res.data
  }
  return await GetHomePageInitDataRequest({ market, host, domain, language })
}

export const GetHomePageInitDataRequest = async ({ market, host, domain, language }) => {
  console.log('HomepageAPI base url and market -> ', { market, host })

  const timing = Date.now()
  console.log('[TIMING GetHomePageInitData Start] = ', timing)

  FlightAPI.defaults.baseURL = host // host
  FlightAPI.defaults.headers['api'] = market === 'tr' ? 'Fs9oQCrRB4LWWMBwZ15DGWMUzIDfUJ2G' : 'OdFdtZY54W2rw87IkpyETYQnQ6Qlidmx'

  HomepageAPI.defaults.timeout = 10000
  FlightAPI.defaults.timeout = 10000

  // /ucak-bileti yalnızca TR (www.enuygun.com). www.wingie.az ve Wingie /api/ kullanır.
  const apiPath = market === 'tr' ? '/ucak-bileti/api/init-homepage.json' : '/api/init-homepage.json'

  try {
    const { data } = await FlightAPI.post(apiPath, { source: 'homepage' })
    console.log('[TIMING GetHomePageInitData End] = ', Date.now() - timing)
    if (data?.error) {
      console.log('GetHomePageInitData 200 status code error ->', data?.error_description)
      return {}
    }
    // Market, domain, language ve host bazlı cache güncelleme
    const cacheKey = `${market}-${domain || market}-${language || 'default'}-${host || 'default'}`
    homepageCache.set(cacheKey, { data, staleTime: timing })
    return data
  } catch (error) {
    console.log('GetHomePageInitData Error ->', error)
    return {}
  }
}

export const CheckFlightReservation = async (params: { pnr: string; lastname: string }) => {
  const timing = Date.now()
  console.log('[TIMING CheckFlightReservation Start] = ', timing)

  const res = await FlightAPI.get('/check-reservation', { params: params })
  console.log('[TIMING CheckFlightReservation End] = ', Date.now() - timing)

  return res.data
}

/** hotel-next-js getReservationDetail ile aynı endpoint. Prod nginx /homepage/api/*'yi homepage'e vermiyor (404). */
export const CheckHotelReservation = async (params: { reference: string; lastName: string; market?: string }): Promise<{ status: boolean }> => {
  const timing = Date.now()
  console.log('[TIMING CheckHotelReservation Start] = ', timing)

  const market = params.market || 'us'
  const searchParams = new URLSearchParams({
    reference: params.reference,
    lastName: params.lastName,
  })

  const baseUrl = domainURL[market as keyof DomainURL] || domainURL.us
  const path = market === 'tr' ? '/otel/rezervasyon-sorgula-kontrol' : '/hotels/rezervasyon-sorgula-kontrol'

  const res = await fetch(`${baseUrl}${path}?${searchParams.toString()}`, {
    headers: {
      Accept: 'application/json',
    },
  })

  console.log('[TIMING CheckHotelReservation End] = ', Date.now() - timing)

  const contentType = res.headers.get('content-type') || ''
  if (!res.ok || !contentType.includes('application/json')) {
    console.error('[CheckHotelReservation] unexpected response', { ok: res.ok, status: res.status, contentType, url: `${baseUrl}${path}` })
    return { status: false }
  }

  return (await res.json()) as { status: boolean }
}

export interface VisaCheckCache {
  [countryCode: string]: {
    isVisaFree: boolean
    visaType?: string
  }
}

export const GetVisaFreeCountries = async (market: string): Promise<VisaCheckCache> => {
  const timing = Date.now()
  console.log('[TIMING GetVisaFreeCountries Start] = ', timing)

  // Sadece Türkiye pazarı için kontrol et
  if (market !== 'tr') {
    return {}
  }

  try {
    const apiBaseUrl = 'https://www.enuygun.com'
    const apiUrl = `${apiBaseUrl}/napi/v1/visa-free-countries/list`

    const response = await fetch(apiUrl)

    if (response.ok) {
      const responseData = await response.json()

      // Tüm ülkeler için cache oluştur
      const visaCache: VisaCheckCache = {}

      // API response'u { data: [...], total: 103 } formatında geliyor
      const dataArray = responseData?.data || responseData

      if (Array.isArray(dataArray)) {
        dataArray.forEach((item: any) => {
          const countryCode = item?.countryCode || item?.country_code

          if (countryCode) {
            const visaType = item?.visaType || ''
            const isVisaFree =
              visaType === 'Vizesiz' ||
              visaType === 'e-Vize' ||
              visaType === 'Kapıda vize' ||
              visaType === 'e-Vize / Kapıda vize' ||
              visaType === 'KETA belgesi isteniyor' ||
              visaType === 'Kısmen vizesiz'

            visaCache[countryCode] = {
              isVisaFree,
              ...(isVisaFree && visaType ? { visaType } : {}),
            }
          }
        })
      }

      return visaCache
    }
  } catch (error) {
    console.error('Visa data loading error:', error)
  }

  return {}
}

export const GetVisaFreePromotedFlights = async (market: string, host: string): Promise<any[]> => {
  const timing = Date.now()
  console.log('[TIMING GetVisaFreePromotedFlights Start] = ', timing)

  // Sadece Türkiye pazarı için kontrol et
  if (market !== 'tr') {
    return []
  }

  try {
    const apiBaseUrl = 'https://www.enuygun.com'
    const apiUrl = `${apiBaseUrl}/napi/v1/visa-free-countries/list`

    const response = await fetch(apiUrl)

    if (response.ok) {
      const responseData = await response.json()

      // API response'u { data: [...], total: 103 } formatında geliyor
      const dataArray = responseData?.data || responseData

      if (Array.isArray(dataArray) && dataArray.length > 0) {
        // API'den gelen veriyi kontrol et ve PromotedFlight formatında olanları filtrele
        // Eğer API'den gelen veri zaten PromotedFlight formatındaysa direkt döndür
        console.log('[VISA_FREE_FLIGHTS] API Response sample:', JSON.stringify(dataArray[0], null, 2))

        const promotedFlights = dataArray.filter((item: any) => {
          // PromotedFlight formatında olmalı: id, departureDate, lowestPrice olmalı
          const isValid = item?.id && item?.departureDate && item?.lowestPrice
          if (!isValid) {
            console.log('[VISA_FREE_FLIGHTS] Invalid item:', item)
          }
          return isValid
        })

        console.log('[TIMING GetVisaFreePromotedFlights End] = ', Date.now() - timing)
        console.log('[VISA_FREE_FLIGHTS] Total items:', dataArray.length, 'Filtered items:', promotedFlights.length)
        return promotedFlights
      }
    }
  } catch (error) {
    console.error('Visa free promoted flights loading error:', error)
  }

  console.log('[TIMING GetVisaFreePromotedFlights End] = ', Date.now() - timing)
  return []
}

export default { getCampaigns }
